import type { SearchMode } from '../searchMode';
import type {
  AvailableParameters,
  SearchParameterAvailability,
} from '../searchParameterAvailability';
import type {
  SearchParameterName,
  SearchParameters,
  SearchParameterValues,
} from '../searchParameters';
import type { ResolvedSearch } from './resolvedSearch';
import { SearchParameter } from './searchParameter';

// Exists for legacyTransition, which restates every value rather than handing
// over a delta. Remove with Redux, when callers pass only what changed.
const differsBeyondPage = (a: SearchParameters, b: SearchParameters) => {
  const { page: _a, ...stated } = a.toJSON();
  const { page: _b, ...other } = b.toJSON();
  const names = new Set([...Object.keys(stated), ...Object.keys(other)]);

  return [...names].some(
    (name) =>
      JSON.stringify(stated[name as keyof typeof stated]) !==
      JSON.stringify(other[name as keyof typeof other])
  );
};

/**
 * A search as it actually runs: its parameters, its mode, and what it offers,
 * always in agreement — and the one way to get from it to the next search.
 *
 * Made by `SearchFactory`, never directly, and every way in ends the same way:
 * the search clears whatever it cannot carry and resolves what it offers from
 * those same parameters. So `parameters`, `mode` and `parameter(name).isOffered`
 * cannot describe three different searches.
 *
 * Immutable. A transition returns a new search; nothing here changes in place.
 */
export class Search {
  /** The resolved parameters, with every existing getter. */
  readonly parameters: SearchParameters;

  readonly #resolver: ResolvedSearch;

  readonly #availability: SearchParameterAvailability;

  readonly #offered: AvailableParameters;

  /**
   * Settles `resolved` into a search: anything it cannot carry is cleared, and
   * what it offers is worked out from the result.
   *
   * Clearing here, rather than trusting every caller to, is what makes the
   * agreement structural. When nothing needs clearing — an entry has already
   * cleared — the view used to check is the one kept, so the mode is derived
   * once.
   */
  constructor(
    resolver: ResolvedSearch,
    availability: SearchParameterAvailability,
    resolved: SearchParameters
  ) {
    const view = availability.for(resolved);
    const cleared = view.clearUnavailable();

    this.parameters = cleared;
    this.#resolver = resolver;
    this.#availability = availability;
    this.#offered = cleared === resolved ? view : availability.for(cleared);
  }

  /** The kind of search this is, worked out once with what it offers. */
  get mode(): SearchMode {
    return this.#offered.mode;
  }

  /**
   * Whether this search offers filtering at all, before any one parameter is
   * asked about. A view with no panel to open asks this; a view deciding which
   * controls to draw asks `parameter(name).isOffered`.
   */
  get offersFilters(): boolean {
    return this.#offered.offersFilters;
  }

  /** One parameter on this search, with whether the search offers it. */
  parameter<N extends SearchParameterName>(
    name: N
  ): SearchParameter<N, Search> {
    return new SearchParameter(
      name,
      this.parameters,
      this.#offered.has(name),
      (next) => this.#transitionTo(next)
    );
  }

  /**
   * The next search, with `changes` applied; `undefined` clears.
   *
   * The one transition. A change that moves the search into another preference
   * scope fills what the visitor remembers for that scope and resolves its
   * sort; any other change applies nothing remembered, so a parameter the
   * visitor just cleared stays cleared. Any change beyond the page starts on
   * page 1 unless it moves the page itself. Either way the result is settled
   * like every other search.
   */
  with(changes: Partial<SearchParameterValues>): Search {
    return this.#transitionTo(this.parameters.with(changes));
  }

  // This search is always `previous`, so a scope change is detected against
  // the search being left — never one a caller has to remember to pass.
  #transitionTo(next: SearchParameters): Search {
    const search = new Search(
      this.#resolver,
      this.#availability,
      this.#resolver.onScopeChange(this.parameters, next)
    );

    return this.#startsOverIn(search.parameters)
      ? new Search(
          this.#resolver,
          this.#availability,
          search.parameters.clear('page')
        )
      : search;
  }

  // A different search starts on its first page; the page it was left on may
  // not exist. Compared after settling, so restating a value this search
  // already cleared is not a change.
  #startsOverIn(next: SearchParameters): boolean {
    return (
      next.page !== 1 &&
      next.page === this.parameters.page &&
      differsBeyondPage(this.parameters, next)
    );
  }
}
